// @flow

import type {InjectIntlProvidedProps} from 'react-intl';
import type {RootState} from '@reducers/types';
import type {PerPage} from './types';

import React, {useCallback, useEffect, useState} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {Breadcrumbs, FormGroup} from '@xsolla/uikit';
import classNames from 'classnames/bind';
import {compose} from 'recompose';
import {submitChargebackProof} from '@reducers/disputes/actions/evidence';
import {
  selectEvidenceUploading,
  selectSettingsMerchantID,
  selectTableRowsCount
} from '@reducers/disputes/selectors';
import {selectEnabledFeatureByName} from '@reducers/features';
import Actions from './actions';
import EvidenceProofModal from './components/evidence-proof-form/evidence-proof-modal';
import FlashMessage from './components/FlashMessage/FlashMessage';
import InlineInfo from './components/inline-info';
import Pagination from './pagination';
import Search, {FORM_NAME as filterFormName} from './search';
import Table from './table';
import style from './style.css';

type Props = {
  +merchantID: number,
  +isAvailable: boolean,
  +rowCount: number,
  +chargebackSubmitting: boolean,
  +onSubmitChargebackProof: (disputeId: number, data: {text: string, files: Array<File>}) => void,
  +onDownloadChargebackFile: (fileId: string, filename: string) => void
} & InjectIntlProvidedProps;

const cx = classNames.bind(style);

const Disputes = (props: Props) => {
  const {
    intl,
    isAvailable,
    rowCount,
    merchantID,
    chargebackSubmitting,
    onSubmitChargebackProof,
    onDownloadChargebackFile
  } = props;

  const [openModalDisputeId, setOpenModalDisputeId] = useState(null);
  const [openModalTransactionId, setOpenModalTransactionId] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (notification) {
      const timeout = setTimeout(() => setNotification(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [notification]);

  const openProofForm = useCallback((disputeId, transactionId) => {
    setOpenModalDisputeId(disputeId);
    setOpenModalTransactionId(transactionId);
  }, []);

  const closeProofForm = useCallback(() => {
    setOpenModalDisputeId(null);
    setOpenModalTransactionId(null);
  }, []);

  const handleChargebackProofSubmit = useCallback(
    (disputeId, data, setNotification, messages) => {
      const adaptedData = {
        text: data.commentary || '',
        files: data.files || []
      };
      onSubmitChargebackProof(
        openModalDisputeId,
        adaptedData,
        notification => {
          setNotification(notification);
          if (notification && notification.type === 'success') {
            closeProofForm();
          }
        },
        messages
      );
    },
    [onSubmitChargebackProof, closeProofForm, openModalDisputeId]
  );

  // Helper function to translate error keys
  const translateError = useCallback(
    errorMessage => {
      // Check if it's a translation key
      if (errorMessage.startsWith('app.support.disputes.errors.')) {
        // Handle special case for not-found with requestID
        if (errorMessage.includes(':')) {
          const [key, requestID] = errorMessage.split(':');
          const template = intl.messages[key];
          return template ? template.replace('{requestID}', requestID) : errorMessage;
        }
        return intl.messages[errorMessage] || errorMessage;
      }
      return errorMessage;
    },
    [intl]
  );

  if (!isAvailable) {
    return <Redirect to="/forbidden" />;
  }

  return (
    <div className={cx('disputes')}>
      {notification && (
        <FlashMessage
          type={notification.type}
          text={
            notification.type === 'error' ? translateError(notification.text) : notification.text
          }
          onClose={() => setNotification(null)}
        />
      )}
      <FormGroup border={true}>
        <div className={cx('disputes__title')}>
          <Breadcrumbs
            items={[
              {
                label: intl.messages['app.support.disputes.title'],
                to: ''
              }
            ]}
          />
          <InlineInfo nameValues={[]} />
        </div>
      </FormGroup>
      <Search merchantID={merchantID} />
      <div className={cx('disputes__element')}>
        <div
          className={cx(
            'disputes__element',
            'disputes__element--horizontal',
            'disputes__element--end',
            'disputes__element--bottom'
          )}>
          <Actions />
        </div>
      </div>
      <Table
        onSubmitChargebackProof={onSubmitChargebackProof}
        onDownloadChargebackFile={onDownloadChargebackFile}
        chargebackProofSubmitting={chargebackSubmitting}
        onOpenProofForm={openProofForm}
        merchantId={merchantID}
      />
      {rowCount > (100: PerPage) && <Pagination filterFormName={filterFormName} />}

      <EvidenceProofModal
        isOpen={!!openModalDisputeId}
        disputeId={openModalDisputeId}
        transactionId={openModalTransactionId}
        onSubmit={handleChargebackProofSubmit}
        onClose={closeProofForm}
        isSubmitting={chargebackSubmitting}
        merchantId={merchantID}
        notification={notification}
        setNotification={setNotification}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  merchantID: selectSettingsMerchantID(state),
  isAvailable: Boolean(selectEnabledFeatureByName(state, 'afs_disputes')),
  rowCount: selectTableRowsCount(state),
  chargebackSubmitting: selectEvidenceUploading(state)
});

const mapDispatchToProps = {
  onSubmitChargebackProof: submitChargebackProof
};

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl);

export default enhance(Disputes);
